import { z } from 'zod';
import { labFlagSchema, labOrderStatusSchema, labPanelSchema, type LabPanel } from './enums.js';

/** Lab contracts (docs/spec.md §4.5, §4.5.1, §7 screen 7). */

export interface LabTest {
  id: number;
  panel: LabPanel;
  code: string;
  nameEn: string;
  nameLa: string | null;
  unit: string | null;
  normalMin: string | null;
  normalMax: string | null;
  normalText: string | null;
  sortOrder: number;
  isDefault: boolean;
}

export const labOrderInputSchema = z.object({
  patientId: z.coerce.number().int().positive(),
  orderDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format'),
  /** "HH:mm". Printed separately from the date on the clinic's report. */
  orderTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/, 'time_format')
    .optional()
    .or(z.literal('')),
  /**
   * Number issued by an outside laboratory. Typed in by hand — the clinic does
   * not control this sequence (confirmed 2026-07-30).
   */
  extLabNo: z.string().trim().max(32).optional().or(z.literal('')),
  note: z.string().trim().max(2000).optional().or(z.literal('')),
  /**
   * The test codes ticked for this order. Each becomes a row in `lab_results`;
   * untick removes the row (docs/spec.md §4.5.1).
   */
  testCodes: z.array(z.string().trim().min(1).max(48)).min(1, 'tests_required'),
});
export type LabOrderInput = z.infer<typeof labOrderInputSchema>;

export const labResultEntrySchema = z.object({
  testCode: z.string().trim().min(1).max(48),
  /** As printed: "83", "Negative", "A". Empty means ordered but not yet run. */
  value: z.string().trim().max(120).optional().or(z.literal('')),
  /** Overrides the computed flag; blank keeps the automatic one. */
  flag: labFlagSchema.optional(),
});

export const labResultsInputSchema = z.object({
  /** The complete ticked set — anything missing here is removed. */
  entries: z.array(labResultEntrySchema),
});
export type LabResultsInput = z.infer<typeof labResultsInputSchema>;

export const labApproveSchema = z.object({
  approve: z.boolean().default(true),
});

export interface LabResultRow {
  testCode: string;
  nameEn: string;
  nameLa: string | null;
  panel: LabPanel;
  unit: string | null;
  normalText: string | null;
  sortOrder: number;
  value: string | null;
  flag: '' | 'L' | 'H';
  reportedByName: string | null;
}

export interface LabOrder {
  id: number;
  labNo: string;
  extLabNo: string | null;
  patientId: number;
  patientName: string;
  patientCode: string;
  patientAge: number | null;
  patientGender: string | null;
  patientExtHn: string | null;
  patientWard: string | null;
  orderedByName: string | null;
  orderDate: string;
  orderTime: string | null;
  status: 'ordered' | 'in_progress' | 'completed' | 'cancelled';
  approvedByName: string | null;
  approvedAt: string | null;
  note: string | null;
  results: LabResultRow[];
}

/** Panel display names, exactly as they appear on the clinic's printed report. */
export const LAB_PANEL_NAMES: Record<LabPanel, string> = {
  chemistry: 'Routine Chemistry',
  cbc: 'Complete Blood Count (CBC)',
  hematology: 'Hematology',
  immunology: 'Immunology',
  urine: 'Urine (VC-LAB)',
};

export const LAB_PANEL_ORDER: readonly LabPanel[] = [
  'chemistry',
  'cbc',
  'hematology',
  'immunology',
  'urine',
];

/**
 * Flags a result Low or High against its reference range.
 *
 * Only numeric values with a numeric bound are flagged. "Negative",
 * "Non Reactive" and blood groups have no ordering, so guessing at one would
 * put a clinically meaningless L or H on a printed report.
 */
export function computeFlag(
  value: string | null | undefined,
  normalMin: string | null,
  normalMax: string | null,
): '' | 'L' | 'H' {
  if (!value) return '';
  const numeric = Number.parseFloat(value.replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numeric)) return '';

  const min = normalMin === null ? null : Number.parseFloat(normalMin);
  const max = normalMax === null ? null : Number.parseFloat(normalMax);

  if (min !== null && Number.isFinite(min) && numeric < min) return 'L';
  if (max !== null && Number.isFinite(max) && numeric > max) return 'H';
  return '';
}

export const labOrderStatusValues = labOrderStatusSchema;
export const labPanelValues = labPanelSchema;
